import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route } from '@/lib/router';
import MainLayout from '@/layouts/MainLayout';
import { QuoteModalProvider } from '@/contexts/QuoteModalContext';
import RequestQuoteModal from '@/components/RequestQuoteModal';

// Eagerly loaded pages
import Home from '@/pages/Home';

// Lazy loaded pages
const ProductsIndex = lazy(() => import('@/pages/products/ProductsIndex'));
const HandheldCategory = lazy(() => import('@/pages/products/HandheldCategory'));
const SmartphoneAcousticImager = lazy(() => import('@/pages/products/SmartphoneAcousticImager'));
const FixedCategory = lazy(() => import('@/pages/products/FixedCategory'));
const ModulesCategory = lazy(() => import('@/pages/products/ModulesCategory'));
const CamerasCategory = lazy(() => import('@/pages/products/CamerasCategory'));
const DAQCategory = lazy(() => import('@/pages/products/DAQCategory'));
const NVHSoftware = lazy(() => import('@/pages/products/NVHSoftware'));
const TrafficMonitoring = lazy(() => import('@/pages/products/TrafficMonitoring'));
const ProductDetail = lazy(() => import('@/pages/products/ProductDetail'));

const CoreModulesCombined = lazy(() => import('@/pages/products/CoreModulesCombined'));
const FModulesCombined = lazy(() => import('@/pages/products/FModulesCombined'));

const ApplicationsIndex = lazy(() => import('@/pages/applications/ApplicationsIndex'));
const ApplicationDetail = lazy(() => import('@/pages/applications/ApplicationDetail'));

const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));

const SupportIndex = lazy(() => import('@/pages/support/SupportIndex'));
// const Downloads = lazy(() => import('@/pages/support/Downloads'));
const FAQ = lazy(() => import('@/pages/support/FAQ'));

// Loading fallback
const PageLoader: React.FC = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin" />
      <p className="mt-4 text-slate-600">Loading...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  return (
    <QuoteModalProvider>
      <Router>
        <MainLayout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />

              {/* Products */}
              <Route path="/products" element={<ProductsIndex />} />
              <Route path="/products/handheld" element={<HandheldCategory />} />
              <Route path="/products/Smartphone-Acoustic-Imager" element={<SmartphoneAcousticImager />} />
              <Route path="/products/Smartphone-Acoustic-Imager/M72-M140" element={<ProductDetail />} />
              <Route path="/products/handheld/des-c136" element={<ProductDetail />} />
              <Route path="/products/handheld/des-t163pad" element={<ProductDetail />} />
              <Route path="/products/handheld/des-c200" element={<ProductDetail />} />
              
              <Route path="/products/fixed" element={<FixedCategory />} />
              <Route path="/products/fixed/des-t168tpt" element={<ProductDetail />} />
              <Route path="/products/modules" element={<ModulesCategory />} />
              <Route path="/products/modules/pcb-modules" element={<ProductDetail />} />
              <Route path="/products/modules/core-o72-o140" element={<CoreModulesCombined />} />
              <Route path="/products/modules/f-series-f72-f140" element={<FModulesCombined />} />
              <Route path="/products/cameras" element={<CamerasCategory />} />
              <Route path="/products/cameras/t144" element={<ProductDetail />} />
              <Route path="/products/cameras/high-performance-array" element={<ProductDetail />} />
              <Route path="/products/daq" element={<DAQCategory />} />
              <Route path="/products/daq/usb-4228" element={<ProductDetail />} />
              <Route path="/products/daq/keydaq-8116" element={<ProductDetail />} />
              <Route path="/products/nvh-software" element={<NVHSoftware />} />
              <Route path="/products/nvh-software/signalpad" element={<NVHSoftware />} />
              <Route path="/products/traffic-monitoring" element={<TrafficMonitoring />} />
              <Route path="/products/traffic-monitoring/horn-capture" element={<ProductDetail />} />
              <Route path="/products/traffic-monitoring/revved-vehicle-capture" element={<ProductDetail />} />

              {/* Applications */}
              <Route path="/applications" element={<ApplicationsIndex />} />
              <Route path="/applications/partial-discharge" element={<ApplicationDetail />} />
              <Route path="/applications/gas-leak-detection" element={<ApplicationDetail />} />
              <Route path="/applications/traffic-noise-monitoring" element={<ApplicationDetail />} />
              <Route path="/applications/nvh-testing" element={<ApplicationDetail />} />

              {/* About & Contact */}
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />

              {/* Support */}
              <Route path="/support" element={<SupportIndex />} />
              {/* <Route path="/support/downloads" element={<Downloads />} /> */}
              <Route path="/support/faq" element={<FAQ />} />

              {/* 404 */}
              <Route path="*" element={
                <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
                    <p className="text-slate-600 mb-6">Page not found</p>
                    <a href="/" className="text-blue-700 hover:underline">Go back home</a>
                  </div>
                </div>
              } />
            </Routes>
          </Suspense>
        </MainLayout>
        <RequestQuoteModal />
      </Router>
    </QuoteModalProvider>
  );
};

export default App;